import React, { useEffect, useState } from 'react';
import { View, Image, StyleSheet, type StyleProp, type ViewStyle } from 'react-native';
import type { ThemeColors } from '../../../../shared/theme';

export const SUBSCRIPTION_PLAN_EDGE_IMAGE_WIDTH_PCT = 0.38;
export const SUBSCRIPTION_PLAN_EDGE_IMAGE_WIDTH = `${Math.round(SUBSCRIPTION_PLAN_EDGE_IMAGE_WIDTH_PCT * 100)}%` as const;
export const SUBSCRIPTION_PLAN_CONTENT_WIDTH = `${100 - Math.round(SUBSCRIPTION_PLAN_EDGE_IMAGE_WIDTH_PCT * 100)}%` as const;
export const SUBSCRIPTION_PLAN_EDGE_MIN_HEIGHT = 116;

type SubscriptionPlanEdgeImageProps = {
  uri: string | null;
  /** Tried when primary `uri` is missing or fails to load. */
  fallback_uri?: string | null;
  border_radius: number;
  colors: ThemeColors;
  fallback?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  min_height?: number;
};

/**
 * Start-edge plan photo for v2 dashboard + subscription rows.
 * Flex sibling (not absolute) so height tracks the card and images paint reliably.
 */
export function SubscriptionPlanEdgeImage({
  uri,
  fallback_uri = null,
  border_radius,
  colors,
  fallback = null,
  style,
  min_height = SUBSCRIPTION_PLAN_EDGE_MIN_HEIGHT,
}: SubscriptionPlanEdgeImageProps) {
  const [active_uri, set_active_uri] = useState<string | null>(uri ?? fallback_uri);
  const [image_error, set_image_error] = useState(false);

  useEffect(() => {
    set_active_uri(uri ?? fallback_uri);
    set_image_error(false);
  }, [uri, fallback_uri]);

  const show_image = Boolean(active_uri) && !image_error;

  const on_image_error = () => {
    if (active_uri && fallback_uri && active_uri !== fallback_uri) {
      set_active_uri(fallback_uri);
      set_image_error(false);
      return;
    }
    set_image_error(true);
  };

  return (
    <View
      style={[
        styles.edge,
        {
          width: SUBSCRIPTION_PLAN_EDGE_IMAGE_WIDTH,
          minHeight: min_height,
          borderTopStartRadius: border_radius,
          borderBottomStartRadius: border_radius,
          backgroundColor: colors.primaryUltra ?? colors.primaryFade,
        },
        style,
      ]}
      pointerEvents="none">
      {show_image ? (
        <Image
          source={{ uri: active_uri! }}
          style={StyleSheet.absoluteFillObject}
          resizeMode="cover"
          fadeDuration={0}
          onError={on_image_error}
        />
      ) : (
        <View style={styles.fallback}>{fallback}</View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  edge: {
    alignSelf: 'stretch',
    overflow: 'hidden',
    position: 'relative',
  },
  fallback: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
